import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Award, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/request-password-reset`, { email });
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } catch (error) {
      // Always show success message to prevent email enumeration
      setSubmitted(true);
      toast.success('Password reset instructions sent!');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">Check Your Email</h2>
            <p className="text-slate-600 mb-6">
              If an account exists with the email <strong className="text-slate-800">{email}</strong>, 
              you will receive a password reset link shortly.
            </p>
            <p className="text-slate-500 text-sm mb-8">
              The link will expire in 1 hour. If you don't see the email, check your spam folder.
            </p>
            <Link to="/login">
              <Button 
                variant="outline" 
                className="w-full h-12 rounded-sm"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>

        {/* Right Panel - Image */}
        <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="text-center text-white">
              <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
                <Mail className="w-14 h-14 text-white" />
              </div>
              <h2 className="font-heading text-4xl font-bold mb-4">Email Sent</h2>
              <p className="text-white/80 text-lg max-w-md mx-auto">
                Check your inbox for instructions to reset your password.
              </p>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 text-slate-600 hover:text-greek-blue mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-greek-blue rounded-sm flex items-center justify-center">
                <Award className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-xl font-bold text-greek-blue">AHEPA Family</h1>
                <p className="text-xs text-slate-500">Scholarship Program</p>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-900">Forgot Password?</h2>
            <p className="text-slate-600 mt-2">
              No worries! Enter your email and we'll send you a link to reset your password.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-sm border-slate-300 focus:border-greek-blue focus:ring-greek-blue"
                data-testid="forgot-password-email-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm font-semibold tracking-wide uppercase"
              data-testid="forgot-password-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>

          <p className="text-center mt-8 text-slate-600">
            Remember your password?{' '}
            <Link to="/login" className="text-greek-blue font-semibold hover:underline" data-testid="forgot-password-login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 bg-greek-blue relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-greek-blue to-greek-blue-dark"></div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <div className="w-24 h-24 bg-greek-gold rounded-sm flex items-center justify-center mx-auto mb-8">
              <Award className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-4xl font-bold mb-4">Reset Password</h2>
            <p className="text-white/80 text-lg max-w-md mx-auto">
              We'll help you regain access to your scholarship account in no time.
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-greek-gold"></div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
